import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import InputField from "../../../Pages/InputField/InputField";
import { Row, Col } from "react-bootstrap";
import axios from "axios";
import baseURL from "../../../../Url/NodeBaseURL";

const STANDARD_PURITIES = [
  "99.9%",
  "91.6%",
  "75.0%",
  "75%",
  "57.5%",
  "37.5%",
  "92.5%",
  "80",
  "manual",
].map((value) => value.toLowerCase());

const normalizePurchasePurityFromData = (data = {}) => {
  const normalizedData = { ...data };
  
  // Parse percentage values
  const parsePercent = (val) => {
    if (val === null || val === undefined || val === "") return 0;
    const s = val.toString().replace("%", "").trim();
    const n = Number(s);
    return Number.isFinite(n) ? n : 0;
  };

  const rawPurchasePurity = data.purchase_purity || data.pur_Purity || "";
  const normalizedRawPurity = rawPurchasePurity
    ? rawPurchasePurity.toString().toLowerCase()
    : "";
  const isStandardPurity =
    normalizedRawPurity && STANDARD_PURITIES.includes(normalizedRawPurity);

  // Calculate purchase_manual_purity by reversing the custom_purity calculation
  // custom_purity = purchase_manual_purity + purchase_wastage_percent + selling_addon_purity
  // Therefore: purchase_manual_purity = custom_purity - purchase_wastage_percent - selling_addon_purity
  let calculatedPurchaseManualPurity = "";
  
  if (data.custom_purity && (normalizedRawPurity === "manual" || !isStandardPurity)) {
    const customPurity = parsePercent(data.custom_purity);
    const purchaseWastage = parsePercent(data.purchase_wastage_percent);
    const sellingAddonPurity = parsePercent(data.selling_addon_purity);
    
    // Reverse calculate: subtract wastage and addon from custom_purity
    const basePurity = customPurity - purchaseWastage - sellingAddonPurity;
    
    if (basePurity > 0) {
      calculatedPurchaseManualPurity = basePurity.toFixed(2);
    }
  }

  // Priority: explicit purchase_manual_purity > calculated from custom_purity > other sources
  const manualPurchasePurity =
    data.purchase_manual_purity ||
    calculatedPurchaseManualPurity ||
    data.pur_purityPercentage ||
    data.purityPercentage ||
    "";

  if (
    manualPurchasePurity &&
    (!rawPurchasePurity ||
      normalizedRawPurity === "manual" ||
      !isStandardPurity)
  ) {
    normalizedData.purchase_purity = "Manual";
    normalizedData.purchase_manual_purity = manualPurchasePurity;
  }

  if (
    normalizedData.purchase_purity &&
    normalizedData.purchase_purity.toString().toLowerCase() === "manual" &&
    manualPurchasePurity
  ) {
    normalizedData.purchase_manual_purity =
      normalizedData.purchase_manual_purity || manualPurchasePurity;
    // Keep custom_purity as is (it's the calculated total)
    normalizedData.custom_purity = normalizedData.custom_purity || data.custom_purity || "";
  }

  return normalizedData;
};

function SubCategoryV2() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { subcategory_id, metal_type, orderDetails, modalData } = state || {};
  const [saveLoader, setSaveLoader] = useState(false);
  const [purchasePurityManuallySet, setPurchasePurityManuallySet] = useState(false);
  
  // Check if opened from tag entry modal
  const isFromTagEntry = location.state?.from === "/purchasetable";

  const [formData, setFormData] = useState(() => {
    const initialData = {
      category_id: "",
      metal_type_id: "",
      vendor_id: "",
      vendor_name: "",
      metal_type: orderDetails?.[0]?.metal_type || metal_type || "",
      category: orderDetails?.[0]?.category || "",
      sub_category_name: "",
      pricing: orderDetails?.[0]?.pricing || "By Weight",
      pricingOpt: "Gross Weight",
      prefix: "",
      purchase_purity: orderDetails?.[0]?.purchase_purity || "",
      purchase_manual_purity:
        orderDetails?.[0]?.purchase_manual_purity ||
        orderDetails?.[0]?.pur_purityPercentage ||
        "",
      purchase_mc_percent: orderDetails?.[0]?.purchase_mc_percent || "",
      purchase_wastage_percent: orderDetails?.[0]?.purchase_wastage_percent || "",
      selling_purity: orderDetails?.[0]?.selling_purity || orderDetails?.[0]?.purchase_purity || "",
      selling_addon_purity: "",
      selling_addon_mc_percent: "",
      selling_addon_wastage_percent: "",
      printing_purity: orderDetails?.[0]?.printing_purity || "",
      selling_mc_percent: "",
      selling_wastage_percent: "",
      custom_purity:
        orderDetails?.[0]?.custom_purity ||
        orderDetails?.[0]?.purchase_manual_purity ||
        "",
      selling_purity_disabled: false,
      purchase_rate: orderDetails?.[0]?.purchase_rate || "",
      selling_rate: "",
      selling_addon_rate: "",
    };
    
    return initialData;
  });

  const [metalOptions, setMetalOptions] = useState([]);
  const [allCategoryOptions, setAllCategoryOptions] = useState([]);
  const [filteredCategoryOptions, setFilteredCategoryOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [prefixError, setPrefixError] = useState("");
  const [existingPrefixes, setExistingPrefixes] = useState([]);

  // Populate purchase fields from orderDetails when available (only in create mode)
  useEffect(() => {
    console.log("order det", orderDetails[0])
    if (orderDetails?.[0] && !subcategory_id) {
      const orderData = orderDetails[0];
      
      setFormData((prevData) => {
        const updates = {};
        
        // Update pricing if provided
        if (orderData.pricing) {
          updates.pricing = orderData.pricing;
        }
        
        // Update purchase_purity - check both field names
        const manualPurchasePurity =
          orderData.purchase_manual_purity ||
          orderData.pur_purityPercentage ||
          orderData.purityPercentage ||
          "";
        let purityValue = orderData.purchase_purity || orderData.pur_Purity;
        const normalizedPurity = purityValue?.toString().toLowerCase() || "";
        const isStandard = normalizedPurity
          ? STANDARD_PURITIES.includes(normalizedPurity)
          : false;

        if (manualPurchasePurity) {
          updates.purchase_manual_purity = manualPurchasePurity;
        }

        if ((normalizedPurity === "manual" || normalizedPurity === "") && manualPurchasePurity) {
          purityValue = "Manual";
        } else if (!isStandard && purityValue) {
          // Treat non-standard values as manual inputs
          updates.purchase_manual_purity =
            manualPurchasePurity || purityValue.replace("%", "");
          purityValue = "Manual";
        }

        if (purityValue !== undefined && purityValue !== null && purityValue !== "") {
          updates.purchase_purity = purityValue;
        }
        
        // Update purchase_mc_percent - check both field names, allow 0 and numeric strings
        const mcValue = orderData.purchase_mc_percent !== undefined ? orderData.purchase_mc_percent : orderData.pur_MC_Per_Gram;
        if (mcValue !== undefined && mcValue !== null && mcValue !== "") {
          updates.purchase_mc_percent = mcValue;
        }
        
        // Update purchase_wastage_percent - check both field names, allow 0 and numeric strings
        const wastageValue = orderData.purchase_wastage_percent !== undefined ? orderData.purchase_wastage_percent : orderData.pur_Wastage_Percentage;
        if (wastageValue !== undefined && wastageValue !== null && wastageValue !== "") {
          updates.purchase_wastage_percent = wastageValue;
        }
        
        // Update purchase_rate - check both field names
        const rateValue = orderData.pieace_cost || orderData.purchase_rate || orderData.pur_rate_cut;
        if (rateValue !== undefined && rateValue !== null && rateValue !== "") {
          updates.purchase_rate = rateValue;
        }

        // Update selling purity default if not set
        if (
          !updates.selling_purity &&
          (orderData.selling_purity || orderData.purchase_purity)
        ) {
          const sellingPurityValue =
            orderData.selling_purity ||
            (purityValue && purityValue !== "Manual" ? purityValue : "");
          if (sellingPurityValue) {
            updates.selling_purity = sellingPurityValue;
          }
        }
        
        // Update printing_purity
        if (orderData.printing_purity !== undefined && orderData.printing_purity !== null && orderData.printing_purity !== "") {
          updates.printing_purity = orderData.printing_purity;
        }
        
        // Check if pricing is "By running tag" and set selling MC and wastage to 0
        const isRunningTag = 
          updates.pricing === "By running tag" || 
          updates.pricing === "By Running Tag" ||
          orderData.pricing === "By running tag" ||
          orderData.pricing === "By Running Tag";
        
        if (isRunningTag) {
          updates.selling_mc_percent = "0";
          updates.selling_wastage_percent = "0";
        }
        
        return { ...prevData, ...updates };
      });
    }
  }, [orderDetails, subcategory_id]);

  const autoPurchasePuritySetRef = useRef(false);

  // Default purchase purity to 22KT / 91.6% for gold/diamond categories
  useEffect(() => {
    if (subcategory_id) return;

    const metalType = formData.metal_type?.toLowerCase() || "";
    const category = formData.category?.toLowerCase() || "";
    const isGoldOrDiamond =
      metalType === "gold" ||
      metalType === "diamond" ||
      category === "gold jewellery" ||
      category === "diamond jewellery";

    if (purchasePurityManuallySet) {
      autoPurchasePuritySetRef.current = false;
      return;
    }

    const hasPurchasePurity =
      formData.purchase_purity &&
      String(formData.purchase_purity).trim() !== "";

    if (purchasePurityManuallySet || hasPurchasePurity) {
      autoPurchasePuritySetRef.current = false;
      return;
    }

    if (isGoldOrDiamond && !autoPurchasePuritySetRef.current) {
      autoPurchasePuritySetRef.current = true;
      setFormData((prev) => ({
        ...prev,
        purchase_purity: "91.6%",
        selling_purity: prev.selling_purity || "91.6%",
      }));
    }
  }, [
    formData.metal_type,
    formData.category,
    formData.purchase_purity,
    subcategory_id,
    purchasePurityManuallySet,
  ]);

  // Fetch metal types
  useEffect(() => {
    const fetchMetalTypes = async () => {
      try {
        const response = await axios.get(`${baseURL}/metaltype`);
        const metalTypes = response.data.map((item) => ({
          value: item.metal_name,
          label: item.metal_name,
          id: item.metal_type_id,
        }));

        setMetalOptions(metalTypes);

        if (metal_type && !subcategory_id) {
          const selectedMetal = metalTypes.find(
            (option) => option.value.toLowerCase() === metal_type.toLowerCase()
          );

          if (selectedMetal) {
            setFormData((prev) => ({
              ...prev,
              metal_type: selectedMetal.value,
              metal_type_id: selectedMetal.id,
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching metal types:", error);
      }
    };
    fetchMetalTypes();
  }, [metal_type, subcategory_id]);

  // Fetch vendors/suppliers
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const response = await axios.get(`${baseURL}/get/account-details`);
        const vendors = response.data
          .filter((item) => item.account_group === "SUPPLIERS")
          .map((item) => ({
            value: item.account_name,
            label: item.account_name,
            id: item.account_id,
          }));
        setVendorOptions(vendors);
      } catch (error) {
        console.error("Error fetching vendors:", error);
      }
    };
    fetchVendors();
  }, []);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${baseURL}/get/products`);
        const categories = response.data.map((item) => ({
          value: item.product_name,
          label: item.product_name,
          id: item.product_id,
          metal_type: item.Category,
        }));
        setAllCategoryOptions(categories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch existing prefixes
  useEffect(() => {
    const fetchExistingPrefixes = async () => {
      try {
        const response = await axios.get(`${baseURL}/subcategory`);
        const prefixes = response.data.data.map((item) => item.prefix);
        setExistingPrefixes(prefixes);
      } catch (error) {
        console.error("Error fetching existing prefixes:", error);
      }
    };
    fetchExistingPrefixes();
  }, []);

  // Function to generate prefix from category and sub category name
  const generatePrefix = (categoryName, subCategoryName) => {
    if (!categoryName || !subCategoryName) return "";

    const getInitials = (str) =>
      str
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase())
        .join("");

    const categoryPrefix = getInitials(categoryName);
    const subCategoryPrefix = getInitials(subCategoryName);

    return `${categoryPrefix}-${subCategoryPrefix}`;
  };

  // Filter categories based on selected metal type
  useEffect(() => {
    const filteredCategories = allCategoryOptions.filter(
      (category) => category.metal_type === formData.metal_type
    );
    setFilteredCategoryOptions(filteredCategories);

    if (!subcategory_id && !formData.category) {
      setFormData((prevData) => ({
        ...prevData,
        category: "",
        category_id: "",
      }));
    }
  }, [
    formData.metal_type,
    allCategoryOptions,
    subcategory_id,
    formData.category,
  ]);

  // Fetch subcategory data when subcategory_id is provided
  useEffect(() => {
    if (
      subcategory_id &&
      metalOptions.length > 0 &&
      allCategoryOptions.length > 0
    ) {
      const fetchSubcategoryData = async () => {
        try {
          const response = await axios.get(
            `${baseURL}/subcategory/${subcategory_id}`
          );
          const subcategoryData = response.data;
          const normalizedData =
            normalizePurchasePurityFromData(subcategoryData);

          // Check if pricing is "By running tag"
          const isRunningTag = 
            normalizedData.pricing === "By running tag" || 
            normalizedData.pricing === "By Running Tag";

          setFormData((prevData) => {
            const updatedData = {
              ...prevData,
              ...normalizedData,
              category: normalizedData.category?.toUpperCase() || "",
              category_id: normalizedData.category_id || "",
            };

            // For "By running tag", set selling MC and wastage to 0
            if (isRunningTag) {
              updatedData.selling_mc_percent = "0";
              updatedData.selling_wastage_percent = "0";
            }

            return updatedData;
          });
        } catch (error) {
          console.error("Error fetching subcategory:", error);
        }
      };
      fetchSubcategoryData();
    }
  }, [subcategory_id, metalOptions, allCategoryOptions]);
//
  // Recalculate custom_purity whenever relevant fields change
  useEffect(() => {
    const parsePercent = (val) => {
      if (val === null || val === undefined || val === "") return 0;
      const s = val.toString().replace("%", "").trim();
      const n = Number(s);
      return Number.isFinite(n) ? n : 0;
    };

    // Calculate purity value
    let purity = 0;
    if (formData.purchase_purity && formData.purchase_purity.toString().toLowerCase() === "manual") {
      purity = parsePercent(formData.purchase_manual_purity);
    } else {
      purity = parsePercent(formData.purchase_purity);
    }

    const purchaseWastage = parsePercent(formData.purchase_wastage_percent);
    const addonPurity = parsePercent(formData.selling_addon_purity);
    //const totalPurity = purity + purchaseWastage + addonPurity;
    const pricing = (formData.pricing || "").trim().toLowerCase();
    const category = (formData.category || "").trim().toLowerCase();

    const isByWeight = pricing === "by weight" && (category === "gold jewellery" || category==="silver jewellery" || category==="diamond jewellery") ;

    const totalPurity = isByWeight
      ? purity + addonPurity // ✔ Exclude wastage when "By Weight"
      : purity + purchaseWastage + addonPurity; // Normal calculation

    // Only update if we have valid values and custom_purity needs to be recalculated
    // Use a small tolerance for floating point comparison
    const currentCustomPurity = parseFloat(formData.custom_purity) || 0;
    const needsUpdate = totalPurity > 0 && Math.abs(currentCustomPurity - totalPurity) > 0.01;
    
    if (needsUpdate) {
      setFormData((prev) => ({
        ...prev,
        custom_purity: totalPurity.toFixed(2),
      }));
    }
  }, [
    formData.purchase_purity,
    formData.purchase_manual_purity,
    formData.purchase_wastage_percent,
    formData.selling_addon_purity,
    formData.custom_purity, // Included to satisfy linter, but condition prevents infinite loop
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let modifiedValue = value;
    if (
      name === "sub_category_name" ||
      name === "prefix" ||
      name === "printing_purity"
    ) {
      modifiedValue = value.toUpperCase();
    }

    if (name === "metal_type") {
      autoPurchasePuritySetRef.current = false;
      setPurchasePurityManuallySet(false);
      const selectedMetal = metalOptions.find(
        (option) => option.value.toLowerCase() === modifiedValue.toLowerCase()
      );

      setFormData((prevData) => ({
        ...prevData,
        metal_type: selectedMetal?.value || "",
        metal_type_id: selectedMetal?.id || "",
        category: "",
        category_id: "",
      }));
    } else if (name === "vendor_name") {
      const selectedVendor = vendorOptions.find(
        (option) => option.value === modifiedValue
      );
      setFormData((prevData) => ({
        ...prevData,
        vendor_name: selectedVendor?.value || "",
        vendor_id: selectedVendor?.id || "",
      }));
    } else if (name === "category") {
      autoPurchasePuritySetRef.current = false;
      setPurchasePurityManuallySet(false);
      const selectedCategory = filteredCategoryOptions.find(
        (option) => option.value === modifiedValue
      );
      setFormData((prevData) => ({
        ...prevData,
        category: selectedCategory?.value || "",
        category_id: selectedCategory?.id || "",
      }));
    } else if (name === "sub_category_name") {
      // Auto-generate prefix when sub category name changes (only in create mode)
      if (!subcategory_id) {
        const generatedPrefix = generatePrefix(
          formData.category,
          modifiedValue
        );

        setFormData((prevData) => ({
          ...prevData,
          [name]: modifiedValue,
          prefix: generatedPrefix,
        }));

        // Validate the generated prefix
        if (generatedPrefix && existingPrefixes.includes(generatedPrefix)) {
          setPrefixError("Prefix already exists");
        } else {
          setPrefixError("");
        }
      } else {
        setFormData((prevData) => ({
          ...prevData,
          [name]: modifiedValue,
        }));
      }
    } else if (name === "prefix") {
      // Validate prefix when manually changed
      if (modifiedValue && existingPrefixes.includes(modifiedValue)) {
        setPrefixError("Prefix already exists");
      } else {
        setPrefixError("");
      }

      setFormData((prevData) => ({
        ...prevData,
        [name]: modifiedValue,
      }));
    } else if (
      name === "purchase_purity" ||
      name === "purchase_manual_purity" ||
      name === "purchase_mc_percent" ||
      name === "purchase_wastage_percent" ||
      name === "selling_addon_mc_percent" ||
      name === "selling_addon_purity" ||
      name === "selling_addon_wastage_percent"
    ) {
      setFormData((prevData) => {
        const updatedData = { ...prevData, [name]: modifiedValue };

        if (name === "purchase_purity") {
          setPurchasePurityManuallySet(true);
          autoPurchasePuritySetRef.current = false;

          if (
            !prevData.selling_purity ||
            prevData.selling_purity === "" ||
            prevData.selling_purity === "Manual"
          ) {
            updatedData.selling_purity = modifiedValue;
          }

          if (
            modifiedValue &&
            typeof modifiedValue === "string" &&
            modifiedValue.toLowerCase() === "manual" &&
            (prevData.custom_purity === "" || prevData.custom_purity === null)
          ) {
            updatedData.custom_purity =
              prevData.purchase_manual_purity ||
              updatedData.purchase_manual_purity ||
              "";
          }
        }

        if (name === "purchase_manual_purity") {
          setPurchasePurityManuallySet(true);
          autoPurchasePuritySetRef.current = false;
          updatedData.custom_purity = modifiedValue;
          if (
            updatedData.purchase_purity &&
            updatedData.purchase_purity.toLowerCase() === "manual" &&
            (!prevData.selling_purity ||
              prevData.selling_purity.toLowerCase() === "manual")
          ) {
            updatedData.selling_purity = "Manual";
          }
        }

        const parsePercent = (val) => {
          if (val === null || val === undefined || val === "") return 0;
          const s = val.toString().replace("%", "").trim();
          const n = Number(s);
          return Number.isFinite(n) ? n : 0;
        };

        const purity = (() => {
          const purchaseValue =
            name === "purchase_purity" ? modifiedValue : prevData.purchase_purity;
          if (
            purchaseValue &&
            typeof purchaseValue === "string" &&
            purchaseValue.toLowerCase() === "manual"
          ) {
            const manualValue =
              name === "purchase_manual_purity"
                ? modifiedValue
                : prevData.purchase_manual_purity;
            return parsePercent(manualValue);
          }
          return parsePercent(purchaseValue);
        })();
        const purchaseMC = parsePercent(
          name === "purchase_mc_percent"
            ? modifiedValue
            : prevData.purchase_mc_percent
        );
        const purchaseWastage = parsePercent(
          name === "purchase_wastage_percent"
            ? modifiedValue
            : prevData.purchase_wastage_percent
        );
        const addonMC = parsePercent(
          name === "selling_addon_mc_percent"
            ? modifiedValue
            : prevData.selling_addon_mc_percent
        );
        const addonWastage = parsePercent(
          name === "selling_addon_wastage_percent"
            ? modifiedValue
            : prevData.selling_addon_wastage_percent
        );
        // Parse addon purity - ensure we use the exact value from input
        const addonPurityValue = name === "selling_addon_purity"
          ? modifiedValue
          : prevData.selling_addon_purity;
        const addonPurity = parsePercent(addonPurityValue);

        // Check if pricing is "By running tag"
        const isRunningTag = 
          prevData.pricing === "By running tag" || 
          prevData.pricing === "By Running Tag";       
        const isByWeight = 
          (prevData.pricing || "").trim().toLowerCase() === "by weight";
        
        // totals
        // const totalPurity = purity + purchaseWastage + addonPurity;
        // PURITY → exclude wastage if pricing is by weight
        const totalPurity = isByWeight
          ? purity + addonPurity
          : purity + purchaseWastage + addonPurity;
        const totalMC = (purchaseMC || 0) + (addonMC || 0);
        const totalWastage = (purchaseWastage || 0) + (addonWastage || 0);

        // Only auto-calculate selling_purity if it's disabled (auto-calculated) or empty
        // Otherwise, preserve the user's manual value
        if (prevData.selling_purity_disabled || !prevData.selling_purity || prevData.selling_purity === "") {
          updatedData.selling_purity = totalPurity.toFixed(2) + "%";
          updatedData.selling_purity_disabled = totalPurity !== 0;
        }
        // If selling_purity is manually set and not disabled, keep it as is
        // selling_purity_disabled remains false

        // Update custom_purity to reflect the calculated total purity
        // This should update even if the field is disabled, as per user requirement
        updatedData.custom_purity = totalPurity > 0 ? totalPurity.toFixed(2) : (updatedData.custom_purity || "");

        // For "By running tag", set selling MC and wastage to 0
        // For other pricing types, use the calculated totals
        // if (isRunningTag) {
        //   updatedData.selling_mc_percent = "0";
        //   updatedData.selling_wastage_percent = "0";
        // } else {
        //   updatedData.selling_mc_percent = totalMC.toFixed(2);
        //   updatedData.selling_wastage_percent = totalWastage.toFixed(2);
        // }
        if (isRunningTag) {
          // Running Tag → always zero
          updatedData.selling_mc_percent = "0";
          updatedData.selling_wastage_percent = "0";

        } else if (isByWeight) {
          // BY WEIGHT:
          // MC → update ONLY when MC fields change
          if (
            name === "purchase_mc_percent" ||
            name === "selling_addon_mc_percent"
          ) {
            updatedData.selling_mc_percent = totalMC.toFixed(2);
          }
          // WASTAGE → update ONLY when wastage fields change
          if (
            name === "purchase_wastage_percent" ||
            name === "selling_addon_wastage_percent"
          ) {
            updatedData.selling_wastage_percent = totalWastage.toFixed(2);
          }
          // If user changed MC, purity, addon MC -> DO NOT touch wastage

        } else {
          // NORMAL PRICING -> original behavior
          updatedData.selling_mc_percent = totalMC.toFixed(2);
          updatedData.selling_wastage_percent = totalWastage.toFixed(2);
        }


        return updatedData;
      });
    } else if (name === "selling_purity" || name === "custom_purity") {
      // When user manually changes selling_purity, mark it as not disabled (manual)
      setFormData((prevData) => {
        const updatedData = { ...prevData, [name]: modifiedValue };
        if (name === "selling_purity") {
          // If user manually changes selling_purity, disable auto-calculation
          updatedData.selling_purity_disabled = false;
        }
        return updatedData;
      });
    } else if (name === "purchase_rate" || name === "selling_addon_rate") {
      setFormData((prevData) => {
        const updatedData = { ...prevData, [name]: modifiedValue };

        const purchaseRate =
          name === "purchase_rate"
            ? parseFloat(modifiedValue) || 0
            : parseFloat(prevData.purchase_rate) || 0;

        const addonRate =
          name === "selling_addon_rate"
            ? parseFloat(modifiedValue) || 0
            : parseFloat(prevData.selling_addon_rate) || 0;

        const sellingRate = addonRate ? purchaseRate + addonRate : purchaseRate;

        updatedData.selling_rate = sellingRate.toFixed(2);

        return updatedData;
      });
    } else if (name === "pricing") {
      // When pricing changes to "By running tag", set selling MC and wastage to 0
      const isRunningTag = 
        modifiedValue === "By running tag" || 
        modifiedValue === "By Running Tag";
      
      setFormData((prevData) => {
        const updatedData = {
          ...prevData,
          [name]: modifiedValue,
        };

        if (isRunningTag) {
          updatedData.selling_mc_percent = "0";
          updatedData.selling_wastage_percent = "0";
        }

        return updatedData;
      });
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: modifiedValue,
      }));
    }
  };

  const handleSubmit = async (e) => {
    setSaveLoader(true);
    e.preventDefault();

    // Check required fields based on pricing
    if (formData.pricing === "By Weight" && !formData.selling_purity) {
      alert("Selling Purity is required when pricing is By Weight");
      setSaveLoader(false);
      return;
    }

    // Check if prefix already exists
    if (prefixError) {
      alert("Please fix the prefix error before submitting");
      setSaveLoader(false);
      return;
    }

    // Check if subcategory is of type "By running tag"
    const isRunningTag = 
      formData.pricing === "By running tag" || 
      formData.pricing === "By Running Tag";

    // Prepare data to submit
    let dataToSubmit;

    // If subcategory is "By running tag", only save the required fields
    if (isRunningTag) {
      dataToSubmit = {
        // Basic identification fields (required for all subcategories)
        category_id: formData.category_id,
        metal_type_id: formData.metal_type_id,
        vendor_id: formData.vendor_id,
        vendor_name: formData.vendor_name,
        metal_type: formData.metal_type,
        category: formData.category,
        sub_category_name: formData.sub_category_name,
        pricing: formData.pricing,
        pricingOpt: formData.pricingOpt,
        prefix: formData.prefix,
        // Only these 4 addon fields for "By running tag" - NO selling_purity, custom_purity, or other selling fields
        selling_addon_purity: formData.selling_addon_purity,
        selling_addon_mc_percent: formData.selling_addon_mc_percent,
        selling_addon_wastage_percent: formData.selling_addon_wastage_percent,
        printing_purity: formData.printing_purity,
        // Set selling MC and wastage to 0 for "By running tag"
        selling_mc_percent: "0",
        selling_wastage_percent: "0",
      };
    } else {
      // For other pricing types, save all fields
      dataToSubmit = { ...formData };
    }

    try {
      if (subcategory_id) {
        await axios.put(`${baseURL}/subcategory/${subcategory_id}`, dataToSubmit);
        alert("Subcategory updated successfully!");
      } else {
        await axios.post(`${baseURL}/subcategory`, dataToSubmit);
        alert("Subcategory created successfully!");
      }
      const from = location.state?.from || "/subcategorytable";
      navigate(from, {
        state: {
          fullSubCategoryData: formData,
          orderDetails: [
            {
              ...location.state?.orderDetails?.[0],
              category: formData.category,
              product_name: formData.sub_category_name,
              metal_type: formData.metal_type,
              pricing: formData.pricing,
              selling_purity: formData.selling_purity || formData.selling_rate,
              mc_per_gram: formData.selling_mc_percent,
              va_percent: formData.selling_wastage_percent,
              custom_purity: formData.custom_purity,
              printing_purity: formData.printing_purity,
              purchase_mc_percent: formData.purchase_mc_percent,
              purchase_wastage_percent: formData.purchase_wastage_percent,
              pur_Purity: formData.purchase_purity || formData.purchase_rate,
              purchase_manual_purity: formData.purchase_manual_purity,
            },
          ],
          selectedProduct: modalData,
        },
      });
    } catch (error) {
      console.error("Error saving subcategory:", error.message);
      alert("Failed to save subcategory. Please try again.");
    } finally {
      setSaveLoader(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="main-container">
      <div className="customer-master-container">
        <h2>
          {subcategory_id ? "Edit Sub Category V2" : "Add Sub Category V2"}
        </h2>
        <form className="customer-master-form" onSubmit={handleSubmit}>
          {/* Row 1: Vendor, Category, Sub Category, Pricing */}
          <Row>
            {/* <Col md={3}>
              <InputField
                label="Vendor"
                name="vendor_name"
                type="select"
                value={formData.vendor_name}
                onChange={handleChange}
                options={vendorOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                required
                autoFocus
              />
            </Col> */}
            <Col md={3}>
              <InputField
                label="Metal Type"
                name="metal_type"
                type="select"
                value={formData.metal_type}
                onChange={handleChange}
                options={metalOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                required
              />
            </Col>
            <Col md={3}>
              <InputField
                label="Category"
                name="category"
                type="select"
                value={formData.category}
                onChange={handleChange}
                options={filteredCategoryOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                required
              />
            </Col>
            <Col md={3}>
              <InputField
                label="Sub Category"
                name="sub_category_name"
                value={formData.sub_category_name}
                onChange={handleChange}
                required
              />
            </Col>
            <Col md={3}>
              <InputField
                label="Pricing"
                name="pricing"
                type="select"
                value={formData.pricing}
                onChange={handleChange}
                options={[
                  { value: "By Weight", label: "By Weight" },
                  { value: "By fixed", label: "By fixed" },
                  { value: "By running tag", label: "By running tag" },
                  ...(formData.pricing &&
                  !["By Weight", "By fixed", "By running tag"].includes(
                    formData.pricing
                  )
                    ? [
                        {
                          value: formData.pricing,
                          label: formData.pricing,
                        },
                      ]
                    : []),
                ]}
              />
            </Col>
          </Row>
          {formData.pricing === "By Weight" && (
            <Row>
              <Col md={3}>
                <InputField
                  label="Pricing By Weight"
                  name="pricingOpt"
                  type="select"
                  value={formData.pricingOpt}
                  onChange={handleChange}
                  options={[
                    { value: "Gross Weight", label: "Gross Weight" },
                    { value: "Net Weight", label: "Net Weight" },
                  ]}
                />
              </Col>
            </Row>
          )}
          <Row className="mt-4">
            <Col md={6}>
              <div className="p-3 border rounded-3">
                <h4 className="mb-3" style={{ marginTop: "-10px" }}>
                  Purchase
                </h4>
                {(formData.pricing === "By Weight" ||
                  formData.pricing === "By running tag" ||
                  formData.pricing === "By Running Tag") && (
                  <>
                    <Row>
                      <Col md={6}>
                        {formData.metal_type === "SILVER" ||
                        formData.metal_type === "OTHERS" ? (
                          <InputField
                            label="Purchase Purity"
                            name="purchase_purity"
                            value={formData.purchase_purity || ""}
                            onChange={handleChange}
                            disabled={isFromTagEntry}
                          />
                        ) : (
                        <InputField
                          label="Purchase Purity"
                          name="purchase_purity"
                          type="select"
                          value={formData.purchase_purity}
                          onChange={handleChange}
                          disabled={isFromTagEntry}
                          options={[
                            { value: "99.9%", label: "24KT / 99.9%" },
                            { value: "91.6%", label: "22KT / 91.6%" },
                            { value: "75.0%", label: "18KT / 75.0%" },
                            { value: "57.5%", label: "14KT / 57.5%" },
                            { value: "37.5%", label: "9KT / 37.5%" },
                            { value: "Manual", label: "Manual" },
                          ]}
                        />
                        )}
                      </Col>
            {formData.purchase_purity &&
              formData.purchase_purity.toLowerCase?.() === "manual" && (
                <Col md={6}>
                  <InputField
                    label="Purchase Manual Purity %"
                    name="purchase_manual_purity"
                    value={formData.purchase_manual_purity || ""}
                    onChange={handleChange}
                    disabled={isFromTagEntry}
                  />
                </Col>
              )}
                      <Col md={6}>
                        {formData.metal_type !== "OTHERS" ? (
                          <InputField
                            label={
                              formData.metal_type === "SILVER"
                                ? "Purchase MC/gm"
                                : "Purchase MC%"
                            }
                            name="purchase_mc_percent"
                            value={formData.purchase_mc_percent}
                            onChange={handleChange}
                            disabled={isFromTagEntry}
                          />
                        ) : null}
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        {formData.metal_type !== "OTHERS" ? (
                          <InputField
                            label="Purchase Wastage%"
                            name="purchase_wastage_percent"
                            value={formData.purchase_wastage_percent}
                            onChange={handleChange}
                            disabled={isFromTagEntry}
                          />
                        ) : null}
                      </Col>
                      <Col md={6}>
                        <InputField
                          label="Prefix"
                          name="prefix"
                          value={formData.prefix}
                          onChange={handleChange}
                          required
                        />
                        {prefixError && (
                          <div
                            className="error-message"
                            style={{
                              color: "red",
                              fontSize: "12px",
                              marginTop: "5px",
                            }}
                          >
                            {prefixError}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </>
                )}
                {formData.pricing === "By fixed" && (
                  <>
                    <Row>
                      <Col md={6}>
                        <InputField
                          label="Purchase Rate"
                          name="purchase_rate"
                          value={formData.purchase_rate || ""}
                          onChange={handleChange}
                        />
                      </Col>
                      <Col md={6}>
                        <InputField
                          label="Prefix"
                          name="prefix"
                          value={formData.prefix}
                          onChange={handleChange}
                          required
                        />
                        {prefixError && (
                          <div
                            className="error-message"
                            style={{
                              color: "red",
                              fontSize: "12px",
                              marginTop: "5px",
                            }}
                          >
                            {prefixError}
                          </div>
                        )}
                      </Col>
                    </Row>
                  </>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="p-3 border rounded-3">
                <h4 className="mb-3" style={{ marginTop: "-10px" }}>
                  Sales
                </h4>
                {(formData.pricing === "By Weight" ||
                  formData.pricing === "By running tag" ||
                  formData.pricing === "By Running Tag") && (
                  <>
                    <Row>
                      {/* <Col md={6}>
                    <InputField
                      label="Pricing"
                      name="selling_pricing"
                      type="select"
                      value={formData.selling_pricing}
                      onChange={handleChange}
                      options={[
                        { value: "By Gross Weight", label: "By Gross Weight" },
                        { value: "By Net Weight", label: "By Net Weight" },
                        ...(formData.selling_pricing &&
                        !["By Gross Weight", "By Net Weight"].includes(
                          formData.selling_pricing
                        )
                          ? [
                              {
                                value: formData.selling_pricing,
                                label: formData.selling_pricing,
                              },
                            ]
                          : []),
                      ]}
                    />
                  </Col> */}
                      <Col md={6}>
                        <InputField
                          label="Selling Add On Purity"
                          name="selling_addon_purity"
                          type="number"
                          value={formData.selling_addon_purity}
                          onChange={handleChange}
                        />
                      </Col>

                      <Col md={6}>
                        <InputField
                          label="Selling Purity"
                          name="selling_purity"
                          type="select"
                          value={formData.selling_purity || ""}
                          onChange={handleChange}
                          options={
                            formData.metal_type === "SILVER"
                              ? [
                                  { value: "92.5", label: "92.5HM" },
                                  { value: "80", label: "80HM" },
                                  { value: "Manual", label: "Manual" },
                                  ...(formData.selling_purity_disabled
                                    ? [
                                        {
                                          value: formData.selling_purity,
                                          label: formData.selling_purity,
                                        },
                                      ]
                                    : []),
                                ]
                              : formData.metal_type === "OTHERS"
                              ? [
                                  { value: "Manual", label: "Manual" },
                                  ...(formData.selling_purity_disabled
                                    ? [
                                        {
                                          value: formData.selling_purity,
                                          label: formData.selling_purity,
                                        },
                                      ]
                                    : []),
                                ]
                              : [
                                  { value: "91.6%", label: "91.6%" },
                                  { value: "99.9%", label: "99.9%" },
                                  { value: "75%", label: "75%" },
                                  { value: "57.5%", label: "57.5%" },
                                  { value: "37.5%", label: "37.5%" },
                                  { value: "Manual", label: "Manual" },
                                  ...(formData.selling_purity_disabled
                                    ? [
                                        {
                                          value: formData.selling_purity,
                                          label: formData.selling_purity,
                                        },
                                      ]
                                    : []),
                                ]
                          }
                          disabled={isFromTagEntry || formData.selling_purity_disabled}
                          required={formData.pricing === "By Weight"}
                        />
                      </Col>

                      {formData.selling_purity === "Manual" && (
                        <Col md={6}>
                          <InputField
                            label="Custom Purity %"
                            name="custom_purity"
                            value={formData.custom_purity || ""}
                            onChange={handleChange}
                            disabled={isFromTagEntry}
                          />
                        </Col>
                      )}
                    </Row>
                    <Row>
                      <Col md={6}>
                        <InputField
                          label={
                            formData.metal_type === "SILVER"
                              ? "Selling Add On MC/gm"
                              : "Selling Add On MC%"
                          }
                          name="selling_addon_mc_percent"
                          value={formData.selling_addon_mc_percent}
                          onChange={handleChange}
                        />
                      </Col>
                      <Col md={6}>
                        <InputField
                          label="Selling Add On Wastage%"
                          name="selling_addon_wastage_percent"
                          value={formData.selling_addon_wastage_percent}
                          onChange={handleChange}
                        />
                      </Col>
                    </Row>
                    {formData.pricing !== "By running tag" &&
                      formData.pricing !== "By Running Tag" && (
                        <Row>
                          <Col md={6}>
                            <InputField
                              label={
                                formData.metal_type === "SILVER"
                                  ? "Selling MC/gm"
                                  : "Selling MC%"
                              }
                              name="selling_mc_percent"
                              value={formData.selling_mc_percent}
                              onChange={handleChange}
                            />
                          </Col>
                          <Col md={6}>
                            <InputField
                              label="Selling Wastage%"
                              name="selling_wastage_percent"
                              value={formData.selling_wastage_percent}
                              onChange={handleChange}
                            />
                          </Col>
                        </Row>
                      )}
                    <Row>
                      <Col md={6}>
                        <InputField
                          label="Printing Purity"
                          name="printing_purity"
                          value={formData.printing_purity}
                          onChange={handleChange}
                          required
                        />
                      </Col>
                    </Row>
                  </>
                )}
                {formData.pricing === "By fixed" && (
                  <>
                    <Row>
                      <Col md={6}>
                        <InputField
                          label="Selling Add On Rate"
                          name="selling_addon_rate"
                          value={formData.selling_addon_rate}
                          onChange={handleChange}
                        />
                      </Col>
                      <Col md={6}>
                        <InputField
                          label="Selling Rate"
                          name="selling_rate"
                          value={formData.selling_rate}
                          onChange={handleChange}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <InputField
                          label="Printing Purity"
                          name="printing_purity"
                          value={formData.printing_purity}
                          onChange={handleChange}
                          required
                        />
                      </Col>
                    </Row>
                  </>
                )}
              </div>
            </Col>
          </Row>
          <div className="sup-button-container">
            <button type="button" className="cus-back-btn" onClick={handleBack}>
              Close
            </button>
            <button type="submit" className="cus-submit-btn">
              {saveLoader ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving
                </>
              ) : subcategory_id ? (
                "Update"
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SubCategoryV2;
